import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../apiBase';
import { fmtReward, fmtWager, fmtDate } from '../lib/wager';

const WALLETS = [
  { key: 'btc', column: 'btc_address', label: 'BTC' },
  { key: 'eth', column: 'eth_address', label: 'ETH' },
  { key: 'ltc', column: 'ltc_address', label: 'LTC' },
  { key: 'sol', column: 'sol_address', label: 'SOL' },
];

/**
 * Confirms a milestone claim and opens the ticket. `claim` = { platform, cycle: 'current' | 'previous',
 * tier, wagered, upgradeOf, paidReward, cycleStart, cycleEnd }; the server re-checks everything.
 */
export default function ClaimModal({ claim, platform, onClose, onDone }) {
  const [profile, setProfile] = useState(null);
  const [payout, setPayout] = useState('platform');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/profile').then((r) => r.json()).then(setProfile).catch(() => setProfile({}));
  }, []);

  const m = platform.milestones[claim.tier - 1];
  const owed = Math.max(0, m.reward - (claim.paidReward || 0));

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch('/api/benefits/claims', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform: platform.id, cycle: claim.cycle, payout, note }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Could not open the claim');
      onDone(json.ticket);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-[#24242e] max-h-[88vh] overflow-y-auto p-6"
        style={{ background: 'linear-gradient(180deg,#14141c,#0c0c12)' }}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-mono-brand text-[11px] text-[#F6BB42] uppercase tracking-[0.25em] font-bold">
              {claim.cycle === 'previous' ? 'Last cycle' : 'This cycle'} · {fmtDate(claim.cycleStart)} – {fmtDate(claim.cycleEnd)}
            </div>
            <h2 className="font-display-lg text-xl text-white mt-1">
              {claim.upgradeOf ? 'Upgrade your claim' : 'Claim'} — {platform.name} milestone {claim.tier}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#888899] hover:text-white text-xl leading-none cursor-pointer">×</button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-lg bg-[#181820] p-3">
            <div className="text-[11px] text-[#888899] uppercase tracking-wider">Verified wager</div>
            <div className="font-mono-brand text-white font-black mt-1">{fmtWager(platform.unit, claim.wagered)}</div>
          </div>
          <div className="rounded-lg bg-[#181820] p-3">
            <div className="text-[11px] text-[#888899] uppercase tracking-wider">Reward</div>
            <div className="font-mono-brand font-black mt-1" style={{ color: '#5ee08a' }}>{fmtReward(platform.unit, owed)}</div>
            {claim.paidReward > 0 && (
              <div className="text-[11px] text-[#7d7d8c] mt-0.5">
                {fmtReward(platform.unit, m.reward)} minus {fmtReward(platform.unit, claim.paidReward)} already paid
              </div>
            )}
          </div>
        </div>

        {claim.upgradeOf ? (
          <p className="text-sm text-[#c9c9d6] mb-5">
            Your open ticket #{claim.upgradeOf} will be moved up to milestone {claim.tier} ({fmtReward(platform.unit, platform.milestones[claim.tier - 1].threshold)} wagered).
          </p>
        ) : (
          <>
            <label className="block text-xs font-bold text-white/80 mb-1.5">Payout</label>
            <select
              value={payout}
              onChange={(e) => setPayout(e.target.value)}
              className="w-full rounded-lg bg-[#181820] border border-[#353535] px-3 py-2.5 text-sm text-white mb-1"
            >
              <option value="platform">Credit on {platform.name}</option>
              {WALLETS.map((w) => (
                <option key={w.key} value={w.key} disabled={!profile?.[w.column]}>
                  {w.label} wallet{profile?.[w.column] ? '' : ' (add it in Settings)'}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-[#7d7d8c] mb-4">Staff pay out every claim by hand after checking it.</p>
          </>
        )}

        <label className="block text-xs font-bold text-white/80 mb-1.5">Message (optional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={1000}
          rows={3}
          placeholder="Anything staff should know"
          className="w-full rounded-lg bg-[#181820] border border-[#353535] px-3 py-2.5 text-sm text-white placeholder:text-[#7d7d8c] mb-4"
        />

        {error && <p className="text-sm text-[#ff544a] mb-3">{error}</p>}
        <button onClick={submit} disabled={busy} className="btn-primary w-full rounded-lg py-3 text-sm">
          {busy ? 'Checking your wagers…' : claim.upgradeOf ? 'Upgrade claim' : 'Open claim ticket'}
        </button>
      </div>
    </div>,
    document.body
  );
}
